import { inject, Injectable } from '@angular/core';
import { Observable, filter } from 'rxjs';
import { Cachier } from '@lib.core/store/cachier';
import { DataState } from '@lib.core/data-state/data-state';
import { ComparisonType } from '@lib.core/charts/comparison-chart';
import { AspectTabularData } from '@lib.core/data/tabular-data';
import { AspectView } from '@lib.core/entities/indicator/indicator-view';
import { AspectSummary } from '@lib.core/entities/indicator/indicator-summary';
import { AspectMetadata } from '@lib.core/entities/indicator/aspect-metadata';
import { AspectFilters } from '@lib.core/entities/indicator/indicator-filters';
import { AspectRanking } from '@lib.core/entities/indicator/indicator-ranking';
import { AspectDatapoint } from '@lib.core/entities/indicator/indicator-datapoint';
import { FactMetadata } from '@lib.core/entities/indicator/fact-metadata';
import { AspectContext } from '@lib.core/entities/indicator/indicator-context';
import { SourceMetadata } from '@lib.core/entities/indicator/source-metadata';
import { DimensionMetadata } from '@lib.core/entities/indicator/dimension-metadata';
import { EChartsGeoJSON } from '@lib.core/entities/echarts/echarts-geojson';
import { AspectApiService } from '@lib.resources/indicator/indicator-api.service';
import { AspectResource } from '@lib.resources/indicator/resources/indicator-resource';


@Injectable({
  providedIn: 'root'
})

export class AspectStore implements AspectResource {
  private apiService = inject(AspectApiService);
  private cache = inject(Cachier);
  private readonly keyBase = 'aspect';
  private readonly keys = new Set<string>();

  private sortAndStringify(obj: object): string {
    return JSON.stringify(obj, Object.keys(obj).sort());
  }

  getAspectMetadataByNameId(nameId: string): Observable<AspectMetadata> {
    const key = `${this.keyBase}_${nameId}-metadata`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectMetadataByNameId(nameId))
      .pipe(
        filter((a: AspectMetadata | null | undefined) => (a != null)),
      );
  }

  getAspectData(hashKey: string, filters: AspectFilters, clientId: string): Observable<AspectDatapoint[]> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_${this.sortAndStringify(filters)}_data`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectData(hashKey, filters, clientId))
      .pipe(
        filter((a: AspectDatapoint[] | null | undefined) => (a != null))
      );
  }

  getAspectTabularData(hashKey: string, filters: AspectFilters, clientId: string): Observable<AspectTabularData> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_${this.sortAndStringify(filters)}_data_tabular`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectTabularData(hashKey, filters, clientId))
      .pipe(
        filter((tabularData: AspectTabularData | null | undefined) => (tabularData != null))
      );
  }

  getAspectTabularRankingData(hashKey: string, clientId: string, comparisonType?: ComparisonType): Observable<AspectTabularData> {
    const key = `${this.keyBase}_${hashKey}_${clientId}}_ranking_tabular`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectTabularRankingData(hashKey, clientId, comparisonType))
      .pipe(
        filter((a: AspectTabularData | null | undefined) => (a != null))
      );
  }

  getAspectRanking(hashKey: string, clientId: string, comparisonType?: ComparisonType): Observable<AspectRanking> {
    const key = `${this.keyBase}_${hashKey}_${clientId}}_ranking`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectRanking(hashKey, clientId, comparisonType))
      .pipe(
        filter((a: AspectRanking | null | undefined) => (a != null))
      );
  }

  getAspectSummary(hashKey: string, filters: AspectFilters, clientId: string, includeKpis = false): Observable<AspectSummary> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_${this.sortAndStringify(filters)}_includeKpis:${includeKpis}_summary`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectSummary(hashKey, filters, clientId, includeKpis))
      .pipe(
        filter((as: AspectSummary | null | undefined) => (as != null))
      );
  }

  getAspectView(instanceHashKey: string, clientId: string): Observable<AspectView> {
    const key = `${this.keyBase}_${instanceHashKey}_${clientId}_view`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectView(instanceHashKey, clientId))
      .pipe(
        filter((a: AspectView | null | undefined) => (a != null))
      );
  }

  getAspectContext(hashKey: string, clientId: string): Observable<AspectContext> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_context`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectContext(hashKey, clientId))
      .pipe(
        filter((ac: AspectContext | null | undefined) => (ac != null))
      );
  }

  getDataAvailability(aspectHashKey: string, clientId: string): Observable<DataState<boolean>> {
    const key = `${this.keyBase}_${aspectHashKey}_${clientId}_data_available`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getDataAvailability(aspectHashKey, clientId))
      .pipe(
        filter((a: DataState<boolean> | null | undefined) => (a != null))
      );
  }

  getAspectMetadata(hashKey: string, clientId: string): Observable<AspectMetadata> {
    const key = `${this.keyBase}_${hashKey}_${clientId}-metadata`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectMetadata(hashKey, clientId))
      .pipe(
        filter((a: AspectMetadata | null | undefined) => (a != null))
      );
  }

  getAspectFactsMetadata(hashKey: string, clientId: string): Observable<FactMetadata[]> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_facts_metadata`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectFactsMetadata(hashKey, clientId))
      .pipe(
        filter((a: FactMetadata[] | null | undefined) => (a != null))
      );
  }

  getAspectDimensionsMetadata(hashKey: string, clientId: string): Observable<DimensionMetadata[]> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_dim_metadata`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectDimensionsMetadata(hashKey, clientId))
      .pipe(
        filter((a: DimensionMetadata[] | null | undefined) => (a != null))
      );
  }

  getAspectSourcesMetadata(hashKey: string, clientId: string): Observable<SourceMetadata[]> {
    const key = `${this.keyBase}_${hashKey}_${clientId}_sources_metadata`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectSourcesMetadata(hashKey, clientId))
      .pipe(
        filter((a: SourceMetadata[] | null | undefined) => (a != null))
      );
  }

  getEChartsGeoJSON(locationId: string, subLocation: string): Observable<EChartsGeoJSON> {
    const key = `${this.keyBase}-getEChartsGeoJSON-${locationId}-${subLocation}`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getEChartsGeoJSON(locationId, subLocation))
      .pipe(filter((entity: EChartsGeoJSON | null | undefined) => entity != null));
  }

  getAspectDataExport(hashKey: string, format: string, clientId: string): Observable<Blob> {
    const key = `${this.keyBase}-getAspectDataExport-${hashKey}-${format}-${clientId}`;
    this.keys.add(key);
    return this.cache.get(key, () => this.apiService.getAspectDataExport(hashKey, format, clientId))
      .pipe(filter((entity: Blob | null | undefined) => entity != null));
  }

  destroyAll() {
    this.keys.forEach((key: string) => this.cache.purge(key));
    this.keys.clear();
  }
}
